import Api from '@/modules/core/api/api.ts';
import { Parameter } from '@/modules/document/models/parameter.ts';
import type { DocumentTypeParameterDto } from '@/modules/document/dto/document-type-parameter.dto.ts';
import type { DocumentTypeParameterOptionDto } from '@/modules/document/dto/document-type-parameter-option.dto.ts';
import type { DocumentTypeDto } from '@/modules/document/dto/document-type.dto.ts';
import type { DocumentFullDto } from '@/modules/document/dto/document.dto.ts';
import type { ProjectDto } from '@/modules/document/dto/project.dto.ts';
import type { UserDto } from '@/modules/document/dto/user.dto.ts';
import type { LinkTypeDto } from '@/modules/document/dto/link-type.dto.ts';
import type { RouteDto } from '@/modules/document/dto/route.dto.ts';
import type { UnspecifiedContractorDto } from '@/modules/document/dto/unspecified-contractor.dto.ts';
import type { ItemsDto } from '@/modules/common/dto/items.dto.ts';
import GatewayApi from '@/modules/core/api/gateway.api.ts';
import { GatewayFilter } from '@/modules/core/utils/gateway-filter.ts';
import CacheGroup from '@/modules/common/enums/cache-group.ts';
import { LinkTypeOption } from '@/modules/common/models/option/link-type-option.ts';
import TextOption from '@/modules/common/models/option/text-option.ts';
import UserOption from '@/modules/common/models/option/user-option.ts';
import { DateTime } from 'luxon';
import { AiEndPoint, LinkEndPoint, RouteEndPoint } from '@/modules/common/enums/end-point.ts';
import DocumentTypeOption from '@/modules/common/models/option/document-type-option.ts';
import ProjectOption from '@/modules/common/models/option/project-option.ts';
import type { TableId } from '@/modules/common/enums/table-id.enum.ts';
import RouteOption from '@/modules/common/models/option/route-option.ts';
import { TypeCode } from '@/modules/common/enums/type-code.ts';
import DocumentOption from '@/modules/common/models/option/document-option.ts';
import { mapMaskFormValue } from '@/modules/document/document-master/utils/mask-value.ts';
import type { MaskFormData } from '@/modules/document/document-master/types/mask.ts';
import { getDeadlineDate } from '@/modules/common/utils/deadline.ts';
import { UnspecifiedContractor } from '@/modules/document/models/unspecified-contractor.ts';
import type { Deadline } from '@/modules/common/types/ui.ts';
import { SummarizeFileAdapter } from '@/modules/common/adapters/summarize-file.adapter.ts';
import { DocumentEndPoint, ProjectEndPoint, UserEndPoint } from '@/modules/document/enums/document-end-point.enum.ts';
import { RequestUtil } from '@/modules/core/utils/request.util.ts';
import { Direction } from '@/modules/common/enums/direction.enum.ts';

export type GetDeadlineParameters = {
  days: number;
  from?: DateTime;
};

export type GetUnspecifiedContractorsParameters = {
  routeId: number;
  typeId: number;
  projectLabel: string;
  content: string | null;
  deadline: Deadline;
  mask: string | null;
  maskFromData: MaskFormData;
};

export default class DocumentMasterApi {
  static DEFAULT_LINK_TYPE = {
    code: 1,
    label: 'document.shared.byDefault',
  };

  private static readonly HINTS_COUNT = 500;

  private static typesInflight: Promise<DocumentTypeOption[]> | null = null;

  private static linkTypesInflight = new Map<
    string,
    Promise<(LinkTypeOption | typeof DocumentMasterApi.DEFAULT_LINK_TYPE)[]>
  >();

  static async getTypes(signal?: AbortSignal): Promise<DocumentTypeOption[]> {
    if (!DocumentMasterApi.typesInflight) {
      DocumentMasterApi.typesInflight = (async () => {
        const response: ItemsDto<DocumentTypeDto> = await Api.post(
          DocumentEndPoint.GetActiveTypes,
          RequestUtil.orderBy('Название', Direction.Asc),
          signal,
          true,
          { group: CacheGroup.DocumentMaster, key: 'TYPES' }
        );

        return response.items
          .map((item) => DocumentTypeOption.createByResponse(item))
          .filter((type) => type.code !== TypeCode.Route);
      })().finally(() => {
        DocumentMasterApi.typesInflight = null;
      });
    }

    return DocumentMasterApi.typesInflight;
  }

  /** @deprecated Используйте @/modules/document/api/project.api.ts getList */
  static async getProjects(signal?: AbortSignal): Promise<ProjectOption[]> {
    const response: ItemsDto<ProjectDto> = await Api.post(
      ProjectEndPoint.GetList,
      RequestUtil.orderBy('Название', Direction.Asc),
      signal,
      true,
      {
        group: CacheGroup.DocumentMaster,
        key: 'PROJECTS',
      }
    );

    return response.items.map((item) => new ProjectOption(item)).filter((project) => !project.disabled);
  }

  static async getParameters(typeId: number, signal?: AbortSignal): Promise<Parameter[]> {
    const response: ItemsDto<DocumentTypeParameterDto> = await Api.post(
      DocumentEndPoint.GetTypeParameters,
      {
        DT_ID: typeId,
      },
      signal,
      true,
      { group: CacheGroup.DocumentMaster, key: `PARAMETERS_${typeId}` }
    );

    return response.items.map((item) => new Parameter(item));
  }

  static async getParameterOptions(typeId: number, number: number, signal?: AbortSignal): Promise<TextOption[]> {
    const response: ItemsDto<DocumentTypeParameterOptionDto> = await Api.post(
      DocumentEndPoint.GetTypeParameterOptions,
      {
        dt_id: typeId,
        number,
      },
      signal,
      true,
      { group: CacheGroup.DocumentMaster, key: `MASK_PARAMETER_${typeId}_${number}` }
    );

    return response.items.map((item) => new TextOption(item));
  }

  static async getHints(id: number, number: number, query?: string, signal?: AbortSignal) {
    const response: ItemsDto<DocumentTypeParameterOptionDto> = await Api.post(
      DocumentEndPoint.GetTypeParameterOptionHints,
      {
        dt_id: id,
        number,
        count: DocumentMasterApi.HINTS_COUNT,
        value: query,
      },
      signal
    );

    return response.items.map((item) => new TextOption(item));
  }

  static async getLinkTypes(linkTypesIds: number[] = [], tableId: TableId, signal?: AbortSignal) {
    if (!linkTypesIds.length) {
      return [DocumentMasterApi.DEFAULT_LINK_TYPE];
    }

    const inflightKey = `${linkTypesIds.join('_')}:${tableId}`;
    const inflight = DocumentMasterApi.linkTypesInflight.get(inflightKey);

    if (inflight) {
      return inflight;
    }

    const request = (async () => {
      const response = await GatewayApi.execute<ItemsDto<LinkTypeDto>>(LinkEndPoint.GetLinkTypes, {
        filter: GatewayFilter.and(GatewayFilter.in('LT_ID', linkTypesIds)),
        signal,
        cacheConfig: { group: CacheGroup.DocumentMaster, key: `LINK_TYPES_${linkTypesIds.join('_')}` },
      });

      const options = response.items
        .map((item) => new LinkTypeOption(item))
        .sort((a, b) => linkTypesIds.indexOf(a.code) - linkTypesIds.indexOf(b.code));

      if (!tableId) {
        return [...options, DocumentMasterApi.DEFAULT_LINK_TYPE];
      }

      return [...options.filter((option) => option.tableIds.includes(tableId)), DocumentMasterApi.DEFAULT_LINK_TYPE];
    })().finally(() => {
      DocumentMasterApi.linkTypesInflight.delete(inflightKey);
    });

    DocumentMasterApi.linkTypesInflight.set(inflightKey, request);
    return request;
  }

  static async getActiveUsers(signal?: AbortSignal): Promise<UserOption[]> {
    const response: ItemsDto<UserDto> = await Api.post(UserEndPoint.GetListActive, {}, signal, true, {
      group: CacheGroup.DocumentMaster,
      key: 'USERS',
    });

    return response.items.map((user) => new UserOption(user));
  }

  static async getRoutes(signal?: AbortSignal): Promise<RouteOption[]> {
    const response: ItemsDto<RouteDto> = await Api.post(
      RouteEndPoint.GetStandard,
      RequestUtil.orderBy('Содержание', Direction.Asc),
      signal,
      true,
      { group: CacheGroup.DocumentMaster, key: `ROUTES` }
    );

    return response.items.map((item) => new RouteOption(item));
  }

  static async getDocuments(query: string, signal?: AbortSignal) {
    const documentId = Number(query);
    const response = await GatewayApi.execute<ItemsDto<DocumentFullDto>>(DocumentEndPoint.GetList, {
      filter: GatewayFilter.and(
        GatewayFilter.or(
          GatewayFilter.contains('Содержание', query),
          Number.isFinite(documentId) ? GatewayFilter.eq('DOC_ID', documentId) : undefined
        )
      ),
      orderBy: [{ field: 'Содержание', direction: Direction.Asc }],
      signal,
    });

    return response.items.map((item) => new DocumentOption(item));
  }

  static async getUnspecifiedContractors(
    parameters: GetUnspecifiedContractorsParameters,
    signal?: AbortSignal
  ): Promise<UnspecifiedContractor[]> {
    const entries = Object.entries(parameters.maskFromData);
    const mappedEntries = entries.map(([key, value]) => [`MASK_PARAM${key}`, mapMaskFormValue(value)]);
    const mappedMask = Object.fromEntries(mappedEntries);

    const response: ItemsDto<UnspecifiedContractorDto> = await Api.post(
      RouteEndPoint.GetUnspecifiedContractors,
      {
        doc_id: parameters.routeId,
        substitutions: {
          DT_ID: parameters.typeId,
          Содержание: parameters.content,
          Срок: getDeadlineDate(parameters.deadline),
          Проект: parameters.projectLabel,
          MASK: parameters.mask,
          ...mappedMask,
        },
      },
      signal
    );

    return response.items.map((item) => new UnspecifiedContractor(item));
  }

  static async summarizeFile(fileId: number, maxLength?: number, signal?: AbortSignal) {
    const response: ItemsDto<Record<string, unknown>> = await Api.post(
      AiEndPoint.SummarizeFile,
      {
        file_id: fileId,
        max_length: maxLength,
      },
      signal
    );

    return SummarizeFileAdapter.map(response);
  }
}
